
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from 'components/ui/card';
import { ScrollArea } from 'components/ui/scroll-area';
import { titleCase, upperCase, formatDateOnly, formatDateTime } from 'lib/utils';
import OrderDetails from './OrderDetails';
import TripDetails from './TripDetails';
import { FTLTrackingData } from './types';
import { CnLinkedLhcsResponse, getTripDetails } from '../api/shipments';

const TRUCK_W = 70; // px
const TRUCK_H = 48; // px
const GAP_PX = 0;

interface CustomProgressBarProps {
    progress: number;
}

const CustomProgressBar: React.FC<CustomProgressBarProps> = ({ progress }) => {
    const pct = Math.max(0, Math.min(progress, 100));
    const truckLeft = `min(${pct}%, calc(100% - ${TRUCK_W / 2}px))`;

    return (
        <div className="relative flex items-center justify-between mb-2 px-1">
            <div className="w-3 h-3 bg-green-500 rounded-full z-10 flex-shrink-0" />

            <div className="flex-1 mx-3 relative flex items-center overflow-visible">
                {pct > 0 && (
                    <div
                        className="h-1 bg-blue-500 absolute left-0 top-1/2 -translate-y-1/2 z-10"
                        style={{ width: `max(0px, calc(${pct}% - ${GAP_PX / 2 + TRUCK_W / 2}px))` }}
                    />
                )}

                <div
                    className="h-0 border-t-2 border-dotted border-gray-300 absolute top-1/2 -translate-y-1/2 z-0"
                    style={{
                        left: `calc(${pct}% + ${GAP_PX / 2 + TRUCK_W / 2}px)`,
                        width: `max(0px, calc(100% - (${pct}% + ${GAP_PX / 2 + TRUCK_W / 2}px)))`,
                        borderTopWidth: '2px',
                    }}
                />

                <div
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 pointer-events-none"
                    style={{ left: truckLeft, width: TRUCK_W, height: TRUCK_H }}
                >
                    <img
                        src="/images/truck.png"
                        alt="Truck"
                        className="w-full h-full object-contain select-none"
                        draggable={false}
                    />
                </div>
            </div>
            <div className="w-3 h-3 bg-gray-300 rounded-full z-10 flex-shrink-0" />
        </div>
    );
};

interface TrackingPageFTLProps {
    data: FTLTrackingData;
}

const TrackingPageFTL: React.FC<TrackingPageFTLProps> = ({ data }) => {
    const [activeTab, setActiveTab] = useState<'order' | 'trip'>('order');
    const [tripData, setTripData] = useState<CnLinkedLhcsResponse | null>(null);
    const [tripLoading, setTripLoading] = useState(false);
    const [tripError, setTripError] = useState<string | null>(null);
    const [tripFetched, setTripFetched] = useState(false);

    const progressPct = data.progress
        ? Math.round((data.progress.covered_distance / data.progress.total_distance) * 100)
        : 0;

    const lastUpdatedFormatted = formatDateTime(data.last_updated);

    const fetchTripData = useCallback(async () => {
        if (!data.cn_number) return;

        try {
            setTripLoading(true);
            setTripError(null);
            const res = await getTripDetails(data.cn_number);
            setTripData(res);
            setTripFetched(true);
        } catch (err) {
            console.error(err);
            setTripError("Failed to load trip details");
        } finally {
            setTripLoading(false);
        }
    }, [data.cn_number]);

    useEffect(() => {
        if (activeTab === 'trip' && !tripFetched) {
            fetchTripData();
        }
    }, [activeTab, tripFetched, fetchTripData]);

    const isOnHold = data.status === 'on_hold';
    const isIncident = !!data.incident;
    const isAwaitingPickup = data.status === 'awaiting_pickup';
    const stockDays = data.stock_since
        ? Math.floor((Date.now() - new Date(data.stock_since).getTime()) / (1000 * 60 * 60 * 24))
        : 0;

    return (
        <>
            <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-6 md:gap-6 mb-8">
                <div className="text-left min-w-0 flex-1">
                    <div className="text-gray-600 text-sm">From</div>
                    <div className="text-lg font-bold text-gray-900 mb-1 break-words">
                        {upperCase(data.consignor)}
                    </div>
                    <div className="text-md text-gray-700 mb-1">
                        left {titleCase(data.origin)}
                    </div>
                    <div className="text-left">
                        <div className="text-sm font-semibold text-gray-900">
                            {data.left_date ? formatDateOnly(data.left_date) : '-'}
                        </div>
                    </div>
                </div>
                <div className="text-left md:text-right min-w-0 flex-1">
                    <div className="text-gray-600 text-sm">To</div>
                    <div className="text-lg font-bold text-gray-900 mb-1 break-words">
                        {upperCase(data.consignee)}
                    </div>
                    <div className="text-md text-gray-700 mb-1">
                        arriving at {titleCase(data.destination)}
                    </div>
                    <div className="text-left md:text-right">
                        <div className="text-sm font-semibold text-gray-900">
                            {data.eta ? formatDateOnly(data.eta) : '-'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-8">
                <CustomProgressBar progress={progressPct} />
                <div className="flex justify-between text-sm text-gray-500 mt-2 px-1">
                    <span>{data.progress?.covered_distance} km travelled</span>
                    <span>{data.progress?.total_distance} km total distance</span>
                    <span>{data.progress?.remaining_distance} km remaining</span>
                </div>
            </div>

            {/* Current Status */}
           <div className={`mt-8 p-4 rounded-lg border ${
                isIncident
                    ? 'bg-red-50 border-red-200'
                    : (isOnHold || isAwaitingPickup)
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-blue-50 border-blue-200'
            }`}>
                <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full animate-pulse ${isIncident ? 'bg-red-500' : (isOnHold || isAwaitingPickup) ? 'bg-orange-500' : 'bg-blue-500'}`}></div>
                   <span className={`text-sm font-medium ${isIncident ? 'text-red-900' : (isOnHold || isAwaitingPickup) ? 'text-orange-900' : 'text-blue-900'
                        }`}>
                        {isIncident
                            ? `${titleCase(data.incident?.incident_type || '')} occurred at ${data.incident?.address || '-'}`
                            : isAwaitingPickup
                            ? `Awaiting Pickup at ${data.current_address} since ${data.stock_since ? formatDateOnly(data.stock_since) : '-'} (${stockDays} days)`
                            : isOnHold
                            ? `Currently on hold at ${data.current_address} since ${data.stock_since ? formatDateOnly(data.stock_since) : '-'} (${stockDays} days)`
                            : `Currently in ${data.current_address}`
                        }
                    </span>
                </div>
                <div className={`text-xs mt-1 ${isIncident ? 'text-red-700' : (isOnHold || isAwaitingPickup) ? 'text-orange-700' : 'text-blue-700'
                    }`}>
                    Last updated: {
                        isIncident
                            ? formatDateTime(data.incident!.incident_date).date + ' ' + formatDateTime(data.incident!.incident_date).time
                            : `${lastUpdatedFormatted.date} ${lastUpdatedFormatted.time}`}
                </div>
            </div>

            {/* Order / Trip Details Section */}
            <div className="mt-6 flex-1 min-h-0">
                <Card className="bg-white shadow-sm h-full flex flex-col">
                    <CardContent className="px-0 pb-6 pt-1 flex-1 min-h-0 flex flex-col">
                        {/* Tabs */}
                        <div className="flex w-full border-b border-gray-200 mb-4">
                            {['order', 'trip'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab as 'order' | 'trip')}
                                    className={`flex-1 text-center py-3 text-sm font-medium relative transition-all duration-200
    ${activeTab === tab
                                            ? 'text-black font-semibold after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[2px] after:bg-black'
                                            : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                                        }`}
                                >
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)} Details
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <ScrollArea className="flex-1 min-h-0 px-6">
                            {activeTab === 'order' ? (
                                <OrderDetails orderDetails={data.order_details} />
                            ) : (
                                <TripDetails data={tripData} error={tripError} loading={tripLoading} />
                            )}
                        </ScrollArea>
                    </CardContent>
                </Card>
            </div>
        </>
    );
};

export default TrackingPageFTL;
